/**
 * Os seis indicadores da linha da lista, em ícone.
 *
 * ## Por que ícone, e por que com dica
 *
 * A lista lateral é a navegação principal do painel, e cabem nela umas quinze
 * linhas de uma vez. Cada palavra escrita numa linha é uma linha a menos na
 * tela. Em ícone cabem seis estados no espaço de duas palavras — e a palavra
 * não sumiu: está no `title`, que aparece ao passar o mouse, e no `aria-label`,
 * que é o que o leitor de tela anuncia.
 *
 * ## A dica carrega o NÚMERO, não o rótulo
 *
 * "Satélites" sozinho não informa nada que o ícone já não diga. A dica diz
 * **"9 satélites · posição de 28/10 08:41:07"** — é o número e a hora que
 * decidem se dá para confiar naquele ponto no mapa. Mesma coisa na bateria
 * (28,1 V de alimentação e 4,1 V de interna) e no alerta (quantos e de quando).
 *
 * ## O vermelho é só para exceção
 *
 * Ignição desligada é estado normal de ônibus na garagem, e pintar isso de
 * vermelho ensina a ignorar vermelho. Vermelho aqui é **alerta na fila de
 * tratativas** — e só. O resto é presença (acende) ou ausência (apagado).
 */
import { getVehicleStatus, STATUS } from "../lib/fleet.ts";
import { formatDateTime, formatVoltage } from "../lib/format.ts";
import type { FleetIndicator, Vehicle } from "../lib/schemas.ts";

function Icon({ path, filled = false }: { path: string; filled?: boolean }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 20 20"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={filled ? 0 : 1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

/** Um indicador: ícone, cor e a frase que explica — sempre a mesma estrutura. */
function Chip({ title, tone, children }: { title: string; tone: string; children: React.ReactNode }) {
  return (
    <span
      title={title}
      aria-label={title}
      className={`inline-flex size-5 items-center justify-center rounded ${tone}`}
    >
      {children}
    </span>
  );
}

const APAGADO = "text-faint";

/**
 * Como o motorista foi identificado (RN-05).
 *
 * Vai na dica junto do nome porque "João da Silva" lido de um crachá e "João da
 * Silva" digitado por alguém no fim do turno não valem o mesmo — e hoje 100% da
 * frota é inferido, porque RFID e bilhetagem ainda não existem.
 */
const ORIGEM: Record<string, string> = {
  manual: "informado à mão",
  rfid: "crachá RFID",
  ticketing: "bilhetagem",
  inferred: "inferido",
};

export function VehicleIndicators({
  vehicle,
  indicator,
}: {
  vehicle: Vehicle;
  indicator: FleetIndicator | undefined;
}) {
  const status = STATUS[getVehicleStatus(vehicle)];
  const alertas = indicator?.openAlerts ?? 0;

  return (
    <span className="flex items-center gap-0.5">
      {/* Conexão: o mesmo estado que o ponto colorido do topo da linha, aqui
          com o nome por extenso na dica. */}
      <Chip title={`Conexão: ${status.label.toLowerCase()}`} tone={status.text}>
        <Icon path="M2 7.5a11 11 0 0 1 16 0M5 11a7 7 0 0 1 10 0" />
      </Chip>

      <Chip
        title={
          vehicle.ignition == null
            ? "Ignição: sem dado"
            : vehicle.ignition
              ? "Ignição ligada"
              : "Ignição desligada"
        }
        tone={vehicle.ignition ? "text-emerald-600 dark:text-emerald-400" : APAGADO}
      >
        <Icon path="M10 2v7M6.5 5a5.5 5.5 0 1 0 7 0" />
      </Chip>

      {/* O único que fica vermelho. Ver o cabeçalho. */}
      <Chip
        title={
          alertas > 0
            ? `${alertas} alerta(s) na fila hoje${
                indicator?.lastAlertAt ? ` · último em ${formatDateTime(indicator.lastAlertAt)}` : ""
              }`
            : "Sem alerta pendente no dia operacional"
        }
        tone={alertas > 0 ? "bg-rose-500/15 text-rose-600 dark:text-rose-400" : APAGADO}
      >
        <Icon path="M10 3a4 4 0 0 1 4 4v4l1.5 2.5h-11L6 11V7a4 4 0 0 1 4-4ZM8.5 16.5a1.5 1.5 0 0 0 3 0" />
      </Chip>

      <Chip
        title={
          vehicle.satellites == null
            ? "Satélites: sem dado nesta posição"
            : `${vehicle.satellites} satélite(s) · posição de ${formatDateTime(vehicle.deviceClock)}`
        }
        tone={satelliteTone(vehicle.satellites)}
      >
        <Icon path="M10 6.5a3.5 3.5 0 0 1 3.5 3.5M10 3a7 7 0 0 1 7 7M6 14l8-8M4.5 12.5 7.5 15.5M12.5 4.5l3 3" />
      </Chip>

      <Chip
        title={
          vehicle.supplyMv == null
            ? "Alimentação: sem dado"
            : `Alimentação ${formatVoltage(vehicle.supplyMv)}${
                vehicle.batteryMv == null ? "" : ` · bateria interna ${formatVoltage(vehicle.batteryMv)}`
              }`
        }
        tone={vehicle.supplyMv == null ? APAGADO : "text-ink"}
      >
        <Icon path="M3 7h11v6H3zM16 9v2" filled={false} />
      </Chip>

      {/* Motorista vinculado é EXCEÇÃO hoje: a identificação automática depende
          da bilhetagem/RFID, que ainda não existe. Por isso quem tem aparece
          destacado — é informação, não enfeite. */}
      <Chip
        title={indicator?.driver ? `Motorista: ${indicator.driver} (${ORIGEM[indicator.driverSource ?? "inferred"]})` : "Sem motorista vinculado"}
        tone={indicator?.driver ? "bg-sky-500/15 text-sky-600 dark:text-sky-400" : APAGADO}
      >
        <Icon path="M10 4a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM4.5 16a5.5 5.5 0 0 1 11 0" />
      </Chip>
    </span>
  );
}

/**
 * A cor do indicador de satélites.
 *
 * Abaixo de quatro não há fixo tridimensional possível — a posição, se existe,
 * é fraca. Entre quatro e seis é fixo de qualidade ruim. Daí para cima, normal.
 * Isto é geometria de GNSS, não limiar escolhido: com três satélites não se
 * resolve altitude, e com menos não se resolve posição.
 */
export function satelliteTone(satellites: number | null | undefined): string {
  if (satellites == null) return APAGADO;
  if (satellites < 4) return "text-rose-600 dark:text-rose-400";
  if (satellites < 7) return "text-amber-600 dark:text-amber-400";
  return "text-ink";
}
